import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Select,
    message,
    Popconfirm,
    Switch,
    Alert,
    Tabs,
    Tag,
    InputNumber
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ClearOutlined,
    ThunderboltOutlined,
    CodeOutlined,
    FileZipOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;
const DEFAULT_GZIP_PATTERN = '(text/css)|(text/ht*)|(javascript)';

const CACHE_MODE_META = {
    url: {
        label: 'URL wildcard',
        placeholder: '/static/* or https://example.com/assets/*'
    },
    'content-type': {
        label: 'Content-Type',
        placeholder: 'image/, text/css, application/javascript, etc.'
    },
    file_ext: {
        label: 'File extension',
        placeholder: 'e.g. jpg|png|gif'
    }
};

/** 'php83' → 'PHP 8.3'(版本值来自节点 1,php 处理器,即实际运行的版本) */
const phpLabel = (v) => {
    const m = /^php(\d)(\d+)$/i.exec(String(v || ''));
    return m ? `PHP ${m[1]}.${m[2]}` : `PHP ${v || ''}`.trim();
};

const PerformanceSettings = ({ vhostId, vhostName }) => {
    const { t } = useTranslation();
    const [cacheList, setCacheList] = useState([]);
    const [cacheLoading, setCacheLoading] = useState(false);
    const [cacheEnabled, setCacheEnabled] = useState(false);
    const [cacheDomains, setCacheDomains] = useState([]);
    const [phpConfig, setPhpConfig] = useState(null);
    const [phpVersions, setPhpVersions] = useState([]);
    const [phpDetail, setPhpDetail] = useState(null);
    const [gzipEnabled, setGzipEnabled] = useState(false);
    const [gzipPattern, setGzipPattern] = useState(DEFAULT_GZIP_PATTERN);
    const [gzipSaving, setGzipSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cacheModalVisible, setCacheModalVisible] = useState(false);
    const [cacheForm] = Form.useForm();
    const [cacheCleanLoading, setCacheCleanLoading] = useState(false);
    const [cacheCleanForm] = Form.useForm();

    const cacheMode = Form.useWatch('mode', cacheForm) || 'url';
    const cacheModeMeta = CACHE_MODE_META[cacheMode] || CACHE_MODE_META.url;

    useEffect(() => {
        if (!vhostName) {
            return;
        }
        loadCacheList();
        loadPhpConfig();
        loadPhpVersions();
        loadPhpDetail();
        loadGzipStatus();
        loadCacheDomains();
    }, [vhostName]);

    const normalizeCacheRule = (rule, index) => {
        let mode = rule.type || 'url';
        if (mode === 'header') {
            mode = 'content-type';
        }
        const meta = CACHE_MODE_META[mode] || CACHE_MODE_META.url;
        return {
            id: rule.id ?? index,
            key: rule.id ?? index,
            mode,
            modeLabel: meta.label,
            value: rule.value || '',
            max_age: Number(rule.max_age ?? 0),
            static: rule.static === 1 || rule.static === '1'
        };
    };

    // ==== 缓存管理 ====
    const loadCacheList = async () => {
        if (!vhostName) return;
        setCacheLoading(true);
        try {
            const response = await hostSharedService.getCacheList(vhostName);
            if (response.success) {
                const payload = response.data || {};
                const listSource = Array.isArray(payload.rules)
                    ? payload.rules
                    : Array.isArray(payload)
                        ? payload
                        : [];
                if (Object.prototype.hasOwnProperty.call(payload, 'enabled')) {
                    setCacheEnabled(!!payload.enabled);
                }
                setCacheList(listSource.map((item, index) => normalizeCacheRule(item, index)));
            } else {
                message.error(response.error || t('Failed to load cache list'));
            }
        } catch (error) {
            message.error(t('Failed to load cache list: {{message}}', { message: error.message }));
            setCacheList([]);
        } finally {
            setCacheLoading(false);
        }
    };

    const handleAddCache = async (values) => {
        if (!vhostName) return;
        try {
            const payload = {
                mode: values.mode,
                cache_value: values.cache_value?.trim(),
                max_age: values.max_age ?? 3600,
                static: values.static ? 1 : 0
            };

            const response = await hostSharedService.addCache(vhostName, payload);
            if (response.success) {
                message.success(t('Cache rule added'));
                setCacheModalVisible(false);
                cacheForm.resetFields();
                loadCacheList();
            } else {
                message.error(response.error || t('Failed to add'));
            }
        } catch (error) {
            message.error(t('Failed to add: {{message}}', { message: error.message }));
        }
    };

    const handleDeleteCache = async (cacheId) => {
        if (!vhostName) return;
        try {
            const response = await hostSharedService.deleteCache(vhostName, cacheId);
            if (response.success) {
                message.success(t('Cache rule deleted'));
                loadCacheList();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.message }));
        }
    };

    const loadCacheDomains = async () => {
        if (!vhostName) return;
        try {
            const response = await hostSharedService.getCacheDomains(vhostName);
            if (response.success) {
                setCacheDomains(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('获取可清理域名失败:', error);
        }
    };

    const handleCacheClean = async (values) => {
        if (!vhostName) return;
        const urlsString = (values.urls || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .join(',');

        if (!urlsString) {
            message.error(t('Please enter at least one URL to purge'));
            return;
        }

        setCacheCleanLoading(true);
        try {
            const response = await hostSharedService.cleanCacheByUrl(vhostName, urlsString);
            if (response.success) {
                message.success(response.message || t('Cache purged'));
                cacheCleanForm.resetFields();
            } else {
                message.error(response.error || t('Cleanup failed'));
            }
        } catch (error) {
            message.error(t('Cleanup failed: {{message}}', { message: error.message }));
        } finally {
            setCacheCleanLoading(false);
        }
    };

    const handleAppendDomain = (domain) => {
        const current = cacheCleanForm.getFieldValue('urls') || '';
        const prefix = current && !current.endsWith('\n') ? `${current}\n` : current;
        const addition = `${prefix}https://${domain}/`;
        cacheCleanForm.setFieldsValue({ urls: addition });
    };

    const handleToggleCache = async (enabled) => {
        if (!vhostName) return;
        try {
            const response = await hostSharedService.toggleCache(vhostName, enabled ? 1 : 0);
            if (response.success) {
                message.success(enabled ? t('Cache control enabled') : t('Cache control disabled'));
                setCacheEnabled(enabled);
            } else {
                message.error(response.error || t('Failed to change cache status'));
            }
        } catch (error) {
            message.error(t('Failed to change cache status: {{message}}', { message: error.message }));
        }
    };

    // ==== PHP配置 ====
    const loadPhpConfig = async () => {
        try {
            const response = await hostSharedService.getPhpConfig(vhostName);
            if (response.success) {
                setPhpConfig(response.data);
            }
        } catch (error) {
            console.error('加载PHP配置失败:', error);
        }
    };

    const loadPhpVersions = async () => {
        try {
            const response = await hostSharedService.getPhpVersions(vhostId);
            if (response.success) {
                setPhpVersions(response.data || []);
            }
        } catch (error) {
            console.error('加载PHP版本列表失败:', error);
        }
    };

    const handleSetPhpVersion = async (version) => {
        try {
            const response = await hostSharedService.setPhpVersion(vhostName, version);
            if (response.success) {
                message.success(t('PHP version switched'));
                loadPhpConfig();
                loadPhpDetail(); // 重新加载PHP详细配置
            } else {
                message.error(response.error || response.message || t('Failed to switch'));
            }
        } catch (error) {
            message.error(t('Failed to switch'));
        }
    };

    // ==== Gzip压缩 ====
    const loadGzipStatus = async () => {
        try {
            const response = await hostSharedService.getGzip(vhostName);
            if (response.success) {
                setGzipEnabled(response.data?.enabled || false);
                setGzipPattern(response.data?.pattern || DEFAULT_GZIP_PATTERN);
            }
        } catch (error) {
            console.error('加载Gzip状态失败:', error);
        }
    };

    const handleToggleGzip = async (enabled) => {
        try {
            const effectivePattern = (gzipPattern && gzipPattern.trim()) || DEFAULT_GZIP_PATTERN;
            const response = enabled
                ? await hostSharedService.enableGzip(vhostName, effectivePattern)
                : await hostSharedService.disableGzip(vhostName);

            if (response.success) {
                message.success(t('Gzip compression: {{status}}', { status: enabled ? t('Enable') : t('Disable') }));
                setGzipEnabled(enabled);
                if (enabled && !gzipPattern) {
                    setGzipPattern(DEFAULT_GZIP_PATTERN);
                }
            } else {
                message.error(response.message || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    const handleSaveGzipPattern = async () => {
        const effectivePattern = (gzipPattern && gzipPattern.trim()) || DEFAULT_GZIP_PATTERN;
        setGzipSaving(true);
        try {
            const response = await hostSharedService.setGzip(vhostName, effectivePattern);
            if (response.success) {
                message.success(t('Gzip matching rules updated'));
                setGzipEnabled(true);
                setGzipPattern(effectivePattern);
            } else {
                message.error(response.message || t('Failed to save'));
            }
        } catch (error) {
            message.error(t('Failed to save'));
        } finally {
            setGzipSaving(false);
        }
    };

    const handleResetGzipPattern = () => {
        setGzipPattern(DEFAULT_GZIP_PATTERN);
    };

    // ==== PHP详细配置 ====
    const loadPhpDetail = async () => {
        try {
            const response = await hostSharedService.getPhpDetail(vhostName);
            if (response.success) {
                setPhpDetail(response.data);
            }
        } catch (error) {
            console.error('加载PHP详细配置失败:', error);
        }
    };

    const handleExtensionChange = (extName, checked) => {
        if (!phpDetail) return;

        const newExtensions = { ...phpDetail.extensions };
        if (newExtensions[extName]) {
            newExtensions[extName] = { ...newExtensions[extName], value: checked ? 1 : 0 };
        }

        setPhpDetail({ ...phpDetail, extensions: newExtensions });
    };

    const handleConfigChange = (configName, value) => {
        if (!phpDetail) return;

        const newConfigs = { ...phpDetail.configs };
        if (newConfigs[configName]) {
            newConfigs[configName] = { ...newConfigs[configName], value: value };
        }

        setPhpDetail({ ...phpDetail, configs: newConfigs });
    };

    const handleSavePhpDetail = async () => {
        try {
            setLoading(true);
            const ext = {};
            const config = {};

            // 收集扩展设置
            Object.keys(phpDetail.extensions).forEach(key => {
                ext[key] = phpDetail.extensions[key].value;
            });

            // 收集配置参数
            Object.keys(phpDetail.configs).forEach(key => {
                config[key] = phpDetail.configs[key].value;
            });

            const response = await hostSharedService.setPhpDetail(vhostName, ext, config);
            if (response.success) {
                message.success(t('PHP configuration updated'));
                loadPhpDetail();
            } else {
                message.error(response.message || response.error || t('Failed to save'));
            }
        } catch (error) {
            message.error(t('Failed to save'));
        } finally {
            setLoading(false);
        }
    };

    const handleResetPhpDetail = async () => {
        try {
            setLoading(true);
            const response = await hostSharedService.resetPhpDetail(vhostName);
            if (response.success) {
                message.success(t('PHP configuration reset'));
                loadPhpDetail();
            } else {
                message.error(response.message || t('Reset failed'));
            }
        } catch (error) {
            message.error(t('Reset failed'));
        } finally {
            setLoading(false);
        }
    };

    const cacheColumns = [
        {
            title: t('Match type'),
            dataIndex: 'modeLabel',
            key: 'modeLabel',
            render: (text) => <Tag color="blue">{t(text)}</Tag>
        },
        {
            title: t('Match content'),
            dataIndex: 'value',
            key: 'value'
        },
        {
            title: t('Cache duration (seconds)'),
            dataIndex: 'max_age',
            key: 'max_age'
        },
        {
            title: t('Static cache'),
            dataIndex: 'static',
            key: 'static',
            render: (enabled) => (
                <Tag color={enabled ? 'green' : 'default'}>{enabled ? t('Yes') : t('No')}</Tag>
            )
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title={t('Delete this cache rule?')}
                    onConfirm={() => handleDeleteCache(record.id)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            )
        }
    ];

    return (
        <Tabs defaultActiveKey="cache">
            <TabPane
                tab={
                    <span>
                        <ThunderboltOutlined />
                        {t('Cache management')}
                    </span>
                }
                key="cache"
            >
                <Card
                    title={t('Cache rules')}
                    extra={
                        <Space>
                            <span>{t('Enable caching')}</span>
                            <Switch checked={cacheEnabled} onChange={handleToggleCache} />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={!cacheEnabled}
                                onClick={() => {
                                    cacheForm.resetFields();
                                    cacheForm.setFieldsValue({
                                        mode: 'url',
                                        cache_value: '',
                                        max_age: 3600,
                                        static: true
                                    });
                                    setCacheModalVisible(true);
                                }}
                            >
                                {t('Add cache rule')}
                            </Button>
                        </Space>
                    }
                >
                    <Alert
                        message={t('About cache rules')}
                        description={t('Set cache rules for specific URL paths to improve access speed.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Table
                        dataSource={cacheList}
                        columns={cacheColumns}
                        rowKey="id"
                        loading={cacheLoading}
                    />
                </Card>

                <Modal
                    title={t('Add cache rule')}
                    open={cacheModalVisible}
                    onCancel={() => {
                        setCacheModalVisible(false);
                        cacheForm.resetFields();
                    }}
                    onOk={() => cacheForm.submit()}
                >
                    <Form
                        form={cacheForm}
                        onFinish={handleAddCache}
                        layout="vertical"
                        initialValues={{ mode: 'url', cache_value: '', max_age: 3600, static: true }}
                    >
                        <Form.Item
                            name="mode"
                            label={t('Match type')}
                            rules={[{ required: true, message: t('Select a match type') }]}
                        >
                            <Select>
                                <Option value="url">{t('URL wildcard')}</Option>
                                <Option value="content-type">Content-Type</Option>
                                <Option value="file_ext">{t('File extension')}</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="cache_value"
                            label={t('Match content')}
                            rules={[{ required: true, message: t('Please enter the content to match') }]}
                            extra={t(cacheModeMeta.placeholder)}
                        >
                            <Input placeholder={t(cacheModeMeta.placeholder)} />
                        </Form.Item>
                        <Form.Item
                            name="max_age"
                            label={t('Cache duration (seconds)')}
                            rules={[{ required: true, message: t('Enter the cache duration') }]}
                        >
                            <InputNumber min={60} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="static" label={t('Static cache')} valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Card title={t('Purge cache')} style={{ marginTop: 16 }}>
                    <Alert
                        message={t('Purge cache for an exact URL')}
                        description={t('Please enter complete access URLs, one per line. The system will immediately send a purge command to the host machine.')}
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    {cacheDomains.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                            {t('Common domains:')}
                            {cacheDomains.map((domain) => (
                                <Tag
                                    key={domain}
                                    color="processing"
                                    style={{ cursor: 'pointer', marginBottom: 4 }}
                                    onClick={() => handleAppendDomain(domain)}
                                >
                                    {domain}
                                </Tag>
                            ))}
                        </div>
                    )}
                    <Form form={cacheCleanForm} layout="vertical" onFinish={handleCacheClean}>
                        <Form.Item
                            name="urls"
                            label={t('URL list')}
                            rules={[{ required: true, message: t('Enter the URL to purge') }]}
                            extra={t('Supports multiple lines. Click a domain above to insert https://domain/')}
                        >
                            <TextArea rows={4} placeholder="https://example.com/path\nhttps://static.example.com/assets/main.js" />
                        </Form.Item>
                        <Button
                            type="primary"
                            icon={<ClearOutlined />}
                            loading={cacheCleanLoading}
                            onClick={() => cacheCleanForm.submit()}
                        >
                            {t('Clean up now')}
                        </Button>
                    </Form>
                </Card>
            </TabPane>

            <TabPane
                tab={
                    <span>
                        <CodeOutlined />
                        {t('PHP settings')}
                    </span>
                }
                key="php"
            >
                <Card style={{ marginBottom: 16 }}>
                    <Alert
                        message={t('PHP version guide')}
                        description={t('Switch PHP versions to meet different application requirements. The service reloads automatically after the change.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    {phpConfig && (
                        <div>
                            <p>{t('Current PHP version:')}
                                {phpConfig.version ? (
                                    <Tag color="blue" style={{ marginLeft: 8 }}>
                                        {phpLabel(phpConfig.version)}
                                    </Tag>
                                ) : (
                                    <Tag color="default" style={{ marginLeft: 8 }}>
                                        {t('Not set')}
                                    </Tag>
                                )}
                            </p>
                            <Space direction="vertical" style={{ marginTop: 16 }}>
                                <p>{t('Switch PHP version:')}</p>
                                <Select
                                    value={phpConfig.version || undefined}
                                    placeholder={t('Select a PHP version')}
                                    style={{ width: 200 }}
                                    onChange={handleSetPhpVersion}
                                    loading={phpVersions.length === 0}
                                >
                                    {phpVersions.map(version => (
                                        <Option key={version} value={version}>
                                            {phpLabel(version)}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </div>
                    )}
                </Card>

                {phpDetail && phpConfig?.version && (
                    <Card
                        title={t('PHP-{{version}} environment settings', { version: phpLabel(phpConfig.version).replace('PHP ', '') })}
                        extra={
                            <Space>
                                <Button onClick={handleResetPhpDetail} loading={loading}>
                                    {t('Reset to default')}
                                </Button>
                                <Button type="primary" onClick={handleSavePhpDetail} loading={loading}>
                                    {t('Save configuration')}
                                </Button>
                            </Space>
                        }
                    >
                        <Alert
                            message={t('PHP extensions and parameters')}
                            description={t('Adjust PHP extensions and runtime settings to suit your app. Click Save to apply your changes.')}
                            type="info"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />

                        {/* PHP扩展 */}
                        <div style={{ marginBottom: 32 }}>
                            <h3 style={{ marginBottom: 16 }}>{t('PHP extensions')}</h3>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                {Object.entries(phpDetail.extensions).map(([key, ext]) => (
                                    <div key={key} style={{
                                        padding: '12px 16px',
                                        background: '#fafafa',
                                        borderRadius: 4,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                                                {ext.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#666' }}>
                                                {ext.description}
                                            </div>
                                        </div>
                                        <Switch
                                            checked={ext.value === 1}
                                            onChange={(checked) => handleExtensionChange(key, checked)}
                                        />
                                    </div>
                                ))}
                            </Space>
                        </div>

                        {/* PHP参数 */}
                        <div>
                            <h3 style={{ marginBottom: 16 }}>{t('PHP parameters')}</h3>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                {Object.entries(phpDetail.configs).map(([key, config]) => (
                                    <div key={key} style={{
                                        padding: '12px 16px',
                                        background: '#fafafa',
                                        borderRadius: 4
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: 8
                                        }}>
                                            <div>
                                                <span style={{ fontWeight: 500 }}>{config.description}</span>
                                                <span style={{ marginLeft: 8, fontSize: 12, color: '#999' }}>
                                                    ({key})
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#666' }}>
                                                {t("Default: {{value}}", { value: config.type === 'switch'
                                                    ? (config.dvalue === 1 ? 'On' : 'Off')
                                                    : (config.type === 'number-M' ? config.dvalue + 'M' : config.dvalue) })}
                                                {config.minlimit && config.maxlimit && (
                                                    <span style={{ marginLeft: 8 }}>
                                                        {t("(Range: {{minlimit}}-{{maxlimit}})", { minlimit: config.minlimit, maxlimit: config.maxlimit })}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            {config.type === 'switch' ? (
                                                <Switch
                                                    checked={config.value === 1}
                                                    onChange={(checked) => handleConfigChange(key, checked ? 1 : 0)}
                                                    checkedChildren="On"
                                                    unCheckedChildren="Off"
                                                />
                                            ) : (
                                                <Input
                                                    type="number"
                                                    value={config.value}
                                                    onChange={(e) => handleConfigChange(key, parseInt(e.target.value) || 0)}
                                                    min={config.minlimit}
                                                    max={config.maxlimit}
                                                    style={{ width: 200 }}
                                                    addonAfter={config.type === 'number-M' ? 'M' : null}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </Space>
                        </div>
                    </Card>
                )}
            </TabPane>

            <TabPane
                tab={
                    <span>
                        <FileZipOutlined />
                        {t('Gzip compression')}
                    </span>
                }
                key="gzip"
            >
                <Card>
                    <Alert
                        message={t('Enable Gzip page compression')}
                        description={t('Compresses responses with a matching Content-Type before sending them: (text/css)|(text/ht*)|(javascript). Regular expressions are supported. Keep the default if you are unsure.')}
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Space align="center">
                            <span>{t('Current status:')}</span>
                            <Tag color={gzipEnabled ? 'green' : 'default'}>
                                {gzipEnabled ? t('Enabled') : t('Disabled')}
                            </Tag>
                            <Switch
                                checked={gzipEnabled}
                                onChange={handleToggleGzip}
                                checkedChildren={t('Enable')}
                                unCheckedChildren={t('Disable')}
                            />
                        </Space>

                        <div>
                            <p style={{ marginBottom: 8 }}>{t('Match Content-Type (regular expressions supported)')}</p>
                            <TextArea
                                rows={2}
                                value={gzipPattern}
                                onChange={(e) => setGzipPattern(e.target.value)}
                                placeholder="(text/css)|(text/ht*)|(javascript)"
                                style={{ fontFamily: 'monospace' }}
                            />
                            <Space style={{ marginTop: 12 }}>
                                <Button type="primary" onClick={handleSaveGzipPattern} loading={gzipSaving}>
                                    {t('Save matching rules')}
                                </Button>
                                <Button onClick={handleResetGzipPattern}>
                                    {t('Restore defaults')}
                                </Button>
                            </Space>
                        </div>
                    </Space>
                </Card>
            </TabPane>
        </Tabs>
    );
};

export default PerformanceSettings;
